import React, { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';

// Cloudflare Turnstile bot check for the sign-up form. The token it produces is
// verified server-side by the Cognito pre-sign-up Lambda (lambda/cognito_pre_signup).
// Site keys are public. On localhost we use Cloudflare's always-pass test key,
// which the real secret on the Lambda will reject - so local sign-ups only work
// against a pool without the trigger attached.
const PROD_SITE_KEY = '0x4AAAAAAFLd3ZynuHw3lM9e';
const TEST_SITE_KEY = '1x00000000000000000000AA';
const SITE_KEY = window.location.hostname === 'localhost' ? TEST_SITE_KEY : PROD_SITE_KEY;

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
export const TURNSTILE_ACTION = 'signup';

let scriptPromise;
function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('Failed to load Turnstile'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

const TurnstileWidget = forwardRef(function TurnstileWidget({ onToken, onError }, ref) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  // Keep latest callbacks without re-rendering the widget
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  onTokenRef.current = onToken;
  onErrorRef.current = onError;

  useImperativeHandle(ref, () => ({
    // Tokens are single-use, so reset after every sign-up attempt
    reset: () => {
      onTokenRef.current?.('');
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  useEffect(() => {
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          action: TURNSTILE_ACTION,
          callback: (token) => onTokenRef.current?.(token),
          'expired-callback': () => onTokenRef.current?.(''),
          'error-callback': () => {
            onTokenRef.current?.('');
            onErrorRef.current?.();
          },
        });
      })
      .catch(() => onErrorRef.current?.());

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  return <div ref={containerRef} className="flex justify-center" />;
});

export default TurnstileWidget;
